import React, { useEffect, useState } from "react";
import SideBar from "../components/SideBar";
import DetailsTopBar from "../components/DetailsTopBar";
import { useNavigate } from "react-router-dom";
import { categoryService } from "../services/categoryService";

function ManageSubCategories() {
  const navigate = useNavigate();
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [subcategories, setSubcategories] = useState([]);
  const [categories, setCategories] = useState([]);
  const itemsPerPage = 6;

  const tabs = [
    { key: "category", label: "Category", path: "/manage-category" },
    { key: "subcategory", label: "Sub Category", path: "/manage-subcategory" },
  ];

  useEffect(() => {
    setCurrentPage(1);
  }, [search, categoryFilter, status]);

  useEffect(() => {
    fetchSubcategories();
    fetchCategories();
  }, [currentPage, search, categoryFilter, status]);

  const fetchCategories = async () => {
    try {
      const response = await categoryService.getAllCategories({ per_page: 100, status: 'active' });
      if (response.categories) {
        setCategories(response.categories);
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchSubcategories = async () => {
    setLoading(true);
    try {
      const params = {
        per_page: itemsPerPage,
        page: currentPage,
        search: search || undefined,
        status: status !== "all" ? status : undefined,
        category_id: categoryFilter !== "all" ? categoryFilter : undefined,
        sort_by: "created_at",
        sort_order: "desc",
        with: "category"
      };

      const response = await categoryService.getAllSubcategories(params);

      if (response.subcategories) {
        const formattedSubcategories = response.subcategories.map((item) => ({
          id: item.id,
          name: item.name,
          slug: item.slug,
          description: item.description,
          thumbnail:
            item.image_url ||
            "https://i.pinimg.com/originals/ec/99/db/ec99dbf221d03cf3c93ea8c0336501a1.jpg",
          images: item.image_url ? [item.image_url] : [],
          status: item.status,
          category_id: item.category_id,
          category_name: item.category?.name || "Unknown",
          created_by: item.created_by,
          created_at: item.created_at,
          updated_at: item.updated_at,
          product_count: item.product_count || 0
        }));

        setSubcategories(formattedSubcategories);
      }
    } catch (error) {
      console.error("Failed to fetch subcategories:", error);
      alert(error.response?.data?.message || "Failed to load subcategories");
    } finally {
      setLoading(false);
    }
  };

  const filteredSubcategories = subcategories.filter((subcategory) => {
    const searchMatch =
      subcategory.name.toLowerCase().includes(search.toLowerCase()) ||
      subcategory.slug.toLowerCase().includes(search.toLowerCase()) ||
      subcategory.category_name.toLowerCase().includes(search.toLowerCase());

    const categoryMatch =
      categoryFilter === "all" || subcategory.category_id.toString() === categoryFilter;
    const statusMatch =
      status === "all" ||
      subcategory.status.toLowerCase() === status.toLowerCase();

    return searchMatch && categoryMatch && statusMatch;
  });

  const totalPages = Math.ceil(filteredSubcategories.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedSubcategories = filteredSubcategories.slice(
    startIndex,
    startIndex + itemsPerPage,
  );

  const handleRowClick = (subcategory) => {
    navigate(`/subcategory-details/${subcategory.id}`);
  };

  const goToCreateAccount = () => {
    navigate("/create-subcategory");
  };

  const handleDelete = async (subcategoryId, e) => {
    e.stopPropagation();
    if (window.confirm("Are you sure you want to delete this subcategory?")) {
      try {
        await categoryService.deleteSubcategory(subcategoryId);
        alert("Subcategory deleted successfully");
        fetchSubcategories();
      } catch (error) {
        alert(error.response?.data?.message || "Failed to delete subcategory");
      }
    }
  };

  const handleStatusChange = async (subcategoryId, currentStatus, e) => {
    e.stopPropagation();
    const newStatus = currentStatus === "active" ? "inactive" : "active";

    try {
      await categoryService.updateSubcategoryStatus(subcategoryId, newStatus);
      alert(`Subcategory ${newStatus} successfully`);
      fetchSubcategories();
    } catch (error) {
      alert(error.response?.data?.message || "Failed to update status");
    }
  };

  return (
    <div className="manage-member-layout">
      <SideBar />
      <div className="main-content">
        <div className="header-bar" style={{ border: 0, boxShadow: "none" }}>
          <div className="header-top">
            <h2 className="header-title">Manage Subcategory</h2>
            <button className="action-btn" onClick={goToCreateAccount}>
              + Add New Subcategory
            </button>
          </div>
          <DetailsTopBar tabs={tabs} activeTab="subcategory" />
          <div className="filters">
            <div className="input-group">
              <label>Filter by category:</label>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="filter-select"
              >
                <option value="all">All Categories</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="input-group">
              <label>Filter by status:</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="filter-select"
              >
                <option value="all">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div className="input-group">
              <label>Search:</label>
              <input
                type="text"
                placeholder="Search by name, slug, or category"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="search-input"
               maxLength={20} />
            </div>
          </div>

          {loading ? (
            <div style={{ textAlign: "center", padding: "20px" }}>
              Loading...
            </div>
          ) : (
            <div className="table-container">
              <table className="member-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Subcategory Name</th>
                    <th>Slug</th>
                    <th>Category</th>
                    {/* <th>Products</th> */}
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedSubcategories.length > 0 ? (
                    paginatedSubcategories.map((subcategory, i) => (
                      <tr
                        key={i}
                        onClick={() => handleRowClick(subcategory)}
                        style={{ cursor: "pointer" }}
                      >
                        <td>{subcategory.id}</td>
                        <td>{subcategory.name}</td>
                        <td>{subcategory.slug}</td>
                        <td>{subcategory.category_name}</td>
                        {/* <td>{subcategory.product_count}</td> */}
                        <td>
                          <span
                            className={`status ${subcategory.status.toLowerCase()}`}
                          >
                            {subcategory.status}
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" style={{ textAlign: "center" }}>
                        No subcategories found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              <div className="pagination">
                <button
                  className="page-btn"
                  onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                >
                  &lt;
                </button>
                {Array.from({ length: totalPages }, (_, i) => (
                  <button
                    key={i}
                    className={`page-btn ${currentPage === i + 1 ? "active" : ""}`}
                    onClick={() => setCurrentPage(i + 1)}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  className="page-btn"
                  onClick={() =>
                    setCurrentPage((p) => Math.min(p + 1, totalPages))
                  }
                >
                  &gt;
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ManageSubCategories;
